import { useEffect, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import TAG_COLOR_OPTIONS from '@constants/tagColors';
import { Tag } from 'types/lead-management';

import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '../../constants/common';

interface EditTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tag: Tag) => void;
  editingItem: Tag | null;
  showColorSelector?: boolean;
  labelName?: string;
}

const EditTagModal = ({
  isOpen,
  onClose,
  onSave,
  editingItem: tag,
  showColorSelector = true,
  labelName = 'Tags'
}: EditTagModalProps) => {
  const [tagName, setTagName] = useState(tag?.name || '');
  const [selectedColor, setSelectedColor] = useState(tag?.color || '#E6DFED');

  // Update internal state when tag prop changes
  useEffect(() => {
    if (tag) {
      setTagName(tag.name || '');
      setSelectedColor(tag.color || '#E6DFED');
    }
  }, [tag]);

  const handleSave = () => {
    if (tag && tagName.trim()) {
      const updatedTag: Tag = {
        ...tag,
        name: tagName.trim(),
        color: showColorSelector ? selectedColor : tag.color
      };
      onSave(updatedTag);
      onClose();
    }
  };

  const handleCancel = () => {
    setTagName(tag?.name || '');
    setSelectedColor(tag?.color || '#E6DFED');
    onClose();
  };

  const handleColorSelect = (colorOption: (typeof TAG_COLOR_OPTIONS)[0]) => {
    setSelectedColor(colorOption.bgColor);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-edit-modal
    >
      <div className="w-[400px] max-w-[90vw] rounded-xl bg-theme p-6 shadow-lg">
        <div className="flex flex-col gap-4">
          <span className="text-base font-semibold">{labelName}</span>
          <input
            type="text"
            value={tagName}
            onChange={(e) => setTagName(e.target.value.toLowerCase())}
            placeholder="Enter tag name..."
            className={`w-full rounded-lg px-4 py-3 text-sm placeholder:text-cofeeNeutralInactive ${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
          />

          {showColorSelector && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-darkGrey">
                Choose colour
              </span>
              <div className="flex items-center gap-2">
                {TAG_COLOR_OPTIONS.map((colorOption) => (
                  <button
                    key={colorOption.bgColor}
                    type="button"
                    className={clsx(
                      'flex size-7 cursor-pointer items-center justify-center rounded transition-all',
                      selectedColor === colorOption.bgColor &&
                        'border-2 border-cofeeGreyBlue11'
                    )}
                    style={{ backgroundColor: colorOption.bgColor }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleColorSelect(colorOption);
                    }}
                    aria-label={`Select ${colorOption.name} color`}
                  >
                    {selectedColor === colorOption.bgColor && (
                      <Icon
                        name="check"
                        className="size-4 text-cofeeGreyBlue9"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              variant="tertiary"
              label="Cancel"
              handleClick={handleCancel}
              className="flex-1"
            />
            <Button
              variant="primary"
              label="Save"
              handleClick={handleSave}
              disabled={!tagName.trim()}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTagModal;
